/* global __BLUEHQ_ENV__ */

import { Map, OrderedMap, Record } from 'immutable';

function present(str) {
  if (str !== undefined && str !== '' && str !== null) {
    return true;
  }
  return false;
}

class CrossChannelParameters extends Record(
  {
    runName: '',
    totalBudget: 0,
    targetUniverse: '',
    upliftScore: '',
    jobDiscount: 0.25,
    targetListSubsetUniverse: '',
    targetListSubsetMode: '',
    modeOptions: new OrderedMap(),
  }) {

  get selectedModeOptions() {
    return this.modeOptions
      .filter(mode => mode.selected);
  }

  get jobName() { return `Cross Channel: ${this.runName}`; }

  get runNameParameter() {
    // Replaces spaces in runName with underscores
    // Giraffe appends the final timestamp
    const tableauKey = new Date().toJSON().replace(/\./gi, '_').replace(/[^\w\s]/gi, '');
    return this.runName.replace(/ /g, '_') + '__TK' + tableauKey;
  }

  get ready() { return true; }

  get valid() {
    const runNameValidation = this.runName !== '';

    const budgetValidation = this.totalBudget !== undefined && this.totalBudget > 0;

    const targetUniverseValidation = this.targetUniverse !== undefined
      && this.targetUniverse !== '';

    const modeValidation = this.selectedModeOptions.size > 0
      && this.selectedModeOptions.every(mode => mode.valid);

    const subsetValidation = ((present(this.targetListSubsetUniverse) &&
                               present(this.targetListSubsetMode) &&
                               this.selectedModeOptions.get(this.targetListSubsetMode)
                              ) || (
                               !present(this.targetListSubsetUniverse) &&
                               !present(this.targetListSubsetMode))
                              );

    return runNameValidation && budgetValidation && targetUniverseValidation &&
           modeValidation && subsetValidation;
  }

  get modeCostsJson() {
    return this.modeOptions
      .map(mode => mode.costJson)
      .filter(elem => (elem !== null))
      .toSetSeq();
  }

  get frequencyCapsJson() {
    return this.selectedModeOptions
      .map(mode => mode.frequencyCapJson)
      .filter(elem => (elem !== null))
      .toSetSeq();
  }

  get modeImpactScalingJson() {
    return this.selectedModeOptions
      .map(mode => mode.impactScalingJson)
      .filter(elem => (elem !== null))
      .toSetSeq();
  }

  get budgetConstraintJson() {
    return this.selectedModeOptions
      .map(mode => mode.budgetConstraintJson)
      .filter(elem => (elem !== null))
      .toSetSeq();
  }

  get modeListJson() {
    return this.selectedModeOptions
      .map(mode => mode.key)
      .toSetSeq();
  }

  get modeDiscountJson() {
    return new Map(this.modeOptions.map(mode => Number(mode.discount)));
  }

  get flightSizeJson() {
    return new Map(this.selectedModeOptions
      .map(mode => Number(mode.flightSize))
      .filter(Number));
  }

  get parametersJson() {
    const json = {
      run_name: this.runNameParameter,
      comment: 'this is a JSON configuration file with parameters for the cross-channel optimization script',
      comment2: 'impact_scores can be set to null, mode names must match exactly to column naming convention in the scores data, since \'_contactability\', \'_impact\' will be appended to strings in mode list',
      mode_list: this.modeListJson,
      target_universe_query: this.targetUniverse,
      discount_f: this.modeDiscountJson,
      cross_channel_discounting: Number(this.jobDiscount),
      total_budget: Number(this.totalBudget),
      budget_constraint_list: this.budgetConstraintJson,
      frequency_caps: this.frequencyCapsJson,
      mode_costs: this.modeCostsJson,
      mode_impact_scaling: this.modeImpactScalingJson,
      flight_size: this.flightSizeJson,
    };

    if (this.upliftScore !== undefined && this.upliftScore !== '') {
      json.uplift_score_query = this.upliftScore;
    }

    if (present(this.targetListSubsetMode) && present(this.targetListSubsetUniverse)) {
      json.target_list_subset_mode = this.targetListSubsetMode;
      json.target_list_subset_table = this.targetListSubsetUniverse;
    }

    return json;
  }

  get configJson() {
    const json = {
      jobType: 'giraffe',
      config: [{
        jobType: 'rrunner',
        config: {
          script_key: 'r/cross_channel.R',
          script_bucket: `bluehq-job-scripts-${__BLUEHQ_ENV__}`,
          script_arguments: this.parametersJson,
        },
      }],
    };

    return json;
  }
}

export default CrossChannelParameters;
